'use client'

import { useEffect, useRef } from 'react'

/**
 * Widget do Cloudflare Turnstile (captcha) — renderizado só quando `NEXT_PUBLIC_TURNSTILE_SITE_KEY` existe.
 * Carrega o script uma vez, renderiza via API explícita e devolve o token pelo `onToken` (null ao expirar/
 * falhar). O token é enviado no header `x-turnstile-token` e verificado no servidor (antiSpam.ts).
 * CSP: `challenges.cloudflare.com` está liberado em script-src + frame-src (next.config.ts).
 */

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove: (id: string) => void
  reset: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Falha ao carregar o Turnstile'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export function TurnstileWidget({
  siteKey,
  onToken,
}: {
  siteKey: string
  onToken: (token: string | null) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        })
      })
      .catch(() => onToken(null))
    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current)
        } catch {
          /* widget já removido */
        }
        widgetId.current = null
      }
    }
  }, [siteKey, onToken])

  return <div ref={ref} className="mb-6" />
}
